import { MAP_REPORTS, MAP_REPORT_CONFIG, type MapReport, type ReportValues } from '@/features/map-layers';

/** Legacy's marker size range in pixels (`mapping.js:27` `_options`). */
export const PIE_MIN_RADIUS = 10;
export const PIE_MAX_RADIUS = 60;

export interface PieSlice {
  report: MapReport;
  color: string;
  /** SVG path of the wedge, or `null` when this report is the whole pie. */
  path: string | null;
}

/**
 * Marker radius, scaled linearly against the largest cluster on the map exactly as
 * legacy does (`getIconSize`, `mapping.js:588-590`) — so sizes read as a comparison
 * between markers, not as an absolute quantity.
 */
export function pieRadius(total: number, maxTotal: number): number {
  if (maxTotal <= 0) return PIE_MIN_RADIUS;

  return Math.round(((PIE_MAX_RADIUS - PIE_MIN_RADIUS) * total) / maxTotal) + PIE_MIN_RADIUS;
}

const pointOnCircle = (radius: number, angle: number) => {
  const x = radius + radius * Math.sin(angle);
  const y = radius - radius * Math.cos(angle);

  return `${x.toFixed(2)} ${y.toFixed(2)}`;
};

/**
 * Wedges for one marker, in legacy's slice order, starting at twelve o'clock and
 * running clockwise. Reports worth nothing are skipped, and a report that holds the
 * whole value gets `path: null` — an SVG arc cannot close a full turn on itself.
 */
export function buildPieSlices(values: ReportValues, radius: number): PieSlice[] {
  const total = MAP_REPORTS.reduce((sum, report) => sum + values[report], 0);
  if (total <= 0) return [];

  const slices: PieSlice[] = [];
  let start = 0;

  for (const report of MAP_REPORTS) {
    const value = values[report];
    if (value <= 0) continue;

    const sweep = (value / total) * 2 * Math.PI;
    const color = MAP_REPORT_CONFIG[report].color;

    if (value === total) {
      slices.push({ report, color, path: null });
      continue;
    }

    const end = start + sweep;
    const largeArc = sweep > Math.PI ? 1 : 0;
    const path =
      `M ${radius} ${radius} L ${pointOnCircle(radius, start)} ` +
      `A ${radius} ${radius} 0 ${largeArc} 1 ${pointOnCircle(radius, end)} Z`;

    slices.push({ report, color, path });
    start = end;
  }

  return slices;
}
